/**
 * @fileoverview Invite HR Admin dialog for Active Clients.
 * @description Takes the bank's first user (first name, optional middle name, last name, email and
 * an Indian mobile number) and invites them (POST /sales/clients/{tenantId}/hr-admin-invitation;
 * the client's owner or a Super Admin, with a fresh sign-in). The invitation's idempotency key
 * outlives the dialog, so a retry after an unknown outcome replays the same request. A refusal
 * (`409 / 4128` already invited, `409 / 1054` that email cannot be invited, a stale sign-in) shows as
 * the dialog's error. Close is held while the request is in flight, and the confirmation runs from
 * the mutation itself, so it survives an unmount. The parent mounts it only while it's open.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useInviteClientHrAdmin } from '@/features/sales/hooks/useInviteClientHrAdmin'
import { INDIA_COUNTRY_CODE } from '@/features/sales/lib/prospect-form.utils'
import {
  inviteHrAdminFormSchema,
  type InviteHrAdminFormValues,
} from '@/features/sales/schemas/invite-hr-admin-form.schema'
import FormField from '@/shared/components/molecules/FormField'
import Modal from '@/shared/components/molecules/Modal'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'
import { MOBILE_NUMBER_INPUT_PROPS } from '@/shared/lib/validation/contact-fields.constants'

import DialogActions from './DialogActions'
import SalesRequestError from './SalesRequestError'

import type {
  HrAdminInvited,
  InviteHrAdminRequest,
} from '@/features/sales/types/sales-client.types'
import type { FormEvent } from 'react'

interface InviteHrAdminDialogProps {
  tenantId: string
  bankName: string
  onClose: () => void
  onInvited: (bankName: string, result: HrAdminInvited) => void
}

/**
 * @description Turns the validated form into the invitation's body (no blank middle name).
 * @param {InviteHrAdminFormValues} values - The validated form values.
 * @returns {InviteHrAdminRequest} The POST body.
 */
function toInviteRequest(values: InviteHrAdminFormValues): InviteHrAdminRequest {
  return {
    firstName: values.firstName,
    ...(values.middleName ? { middleName: values.middleName } : {}),
    lastName: values.lastName,
    email: values.email,
    countryCode: INDIA_COUNTRY_CODE,
    mobileNumber: values.mobileNumber,
  }
}

/**
 * @component
 * @param {InviteHrAdminDialogProps} props - The client and the handlers.
 * @param {string} props.tenantId - The client whose HR Admin is invited.
 * @param {string} props.bankName - The bank's name, shown as the subtitle.
 * @param {() => void} props.onClose - Called when the dialog is dismissed.
 * @param {(bankName: string, result: HrAdminInvited) => void} props.onInvited - Called once the
 * invitation is sent.
 */
function InviteHrAdminDialog({ tenantId, bankName, onClose, onInvited }: InviteHrAdminDialogProps) {
  const { t } = useTranslation()
  const invite = useInviteClientHrAdmin(
    /**
     * @description Reports the sent invitation to the page, even if this dialog has unmounted.
     * @param {HrAdminInvited} result - What the invitation answered.
     */
    (result) => onInvited(bankName, result),
  )
  const close = useGuardedClose(onClose, invite.isPending)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteHrAdminFormValues>({
    resolver: zodResolver(inviteHrAdminFormSchema),
    defaultValues: { firstName: '', middleName: '', lastName: '', email: '', mobileNumber: '' },
  })

  /**
   * @description Sends the validated invitation; the mutation reports the result.
   * @param {InviteHrAdminFormValues} values - The form's values.
   */
  const onValid = (values: InviteHrAdminFormValues) => {
    invite.mutate({ tenantId, request: toInviteRequest(values) })
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  /**
   * @description Translates a field's validation message, if it has one.
   * @param {keyof InviteHrAdminFormValues} key - The field.
   * @returns {string | undefined} The message, or undefined when the field is valid.
   */
  const message = (key: keyof InviteHrAdminFormValues) => {
    const text = errors[key]?.message
    return text ? t(text) : undefined
  }
  const f = 'sales.activeClients.invite.fields'

  return (
    <Modal
      open
      onClose={close}
      data-testid="invite-hr-admin-dialog"
      title={t('sales.activeClients.invite.title')}
      subtitle={bankName}
    >
      <form
        onSubmit={onSubmit}
        noValidate
        className="grid grid-cols-1 gap-4 sm:grid-cols-2"
        data-testid="invite-hr-admin-form"
      >
        <p className="col-span-full text-compact text-slate">
          {t('sales.activeClients.invite.hint')}
        </p>
        <FormField
          id="inviteFirstName"
          data-testid="invite-hr-admin-first-name-input"
          size="md"
          label={`${t(`${f}.firstName`)} *`}
          error={message('firstName')}
          {...register('firstName')}
        />
        <FormField
          id="inviteMiddleName"
          data-testid="invite-hr-admin-middle-name-input"
          size="md"
          label={t(`${f}.middleName`)}
          error={message('middleName')}
          {...register('middleName')}
        />
        <FormField
          id="inviteLastName"
          data-testid="invite-hr-admin-last-name-input"
          size="md"
          label={`${t(`${f}.lastName`)} *`}
          error={message('lastName')}
          {...register('lastName')}
        />
        <FormField
          id="inviteEmail"
          data-testid="invite-hr-admin-email-input"
          size="md"
          type="email"
          label={`${t(`${f}.email`)} *`}
          error={message('email')}
          {...register('email')}
        />
        <FormField
          id="inviteMobile"
          data-testid="invite-hr-admin-mobile-input"
          size="md"
          label={`${t(`${f}.mobileNumber`)} *`}
          error={message('mobileNumber')}
          {...MOBILE_NUMBER_INPUT_PROPS}
          {...register('mobileNumber')}
        />
        <div className="col-span-full flex flex-col gap-3">
          {invite.isError && <SalesRequestError error={invite.error} />}
          <DialogActions
            testIdPrefix="invite-hr-admin"
            cancelLabel={t('sales.pipeline.form.cancel')}
            confirmLabel={t('sales.activeClients.invite.submit')}
            onCancel={close}
            confirmType="submit"
            isLoading={invite.isPending}
            disabled={false}
          />
        </div>
      </form>
    </Modal>
  )
}

export default InviteHrAdminDialog
